import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StatisticsManager } from '../statistics-manager.js';
import { AudioManager } from '../audio-manager.js';
import { UIManager } from '../ui-manager.js';
import { ChallengeManager } from '../challenge-manager.js';
import { SpeechManager } from '../speech-manager.js';
import { SessionManager } from '../session-manager.js';
import { MusicTheory } from '../music-theory.js';
import SpeechRecognizer from '../speech-recognizer.ts';
import { InputMode } from '../components/InputModeToggle';
import { ChallengeType } from '../challenge-types';

export interface CombinationStats {
  key: string;
  degree: number;
  accuracy: number;
  averageTime: number;
  attempts: number;
  recentTrend: string;
}

export const useAppLogic = () => {
  // Managers
  const statsManagerRef = useRef<StatisticsManager>(new StatisticsManager());
  const audioManagerRef = useRef<AudioManager>(new AudioManager());
  const uiManagerRef = useRef<UIManager | null>(null);
  const challengeManagerRef = useRef<ChallengeManager | null>(null);
  const speechManagerRef = useRef<SpeechManager | null>(null);
  const sessionManagerRef = useRef<SessionManager | null>(null);

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const [challengeText, setChallengeText] = useState('');
  const [status, setStatus] = useState({ state: 'idle', message: 'Idle' });
  const [isRecording, setIsRecording] = useState(false);
  const [lastResult, setLastResult] = useState({ text: '-', time: '-' });
  const [stats, setStats] = useState({
    accuracy: '-%',
    total: '0',
    avgTime: '-s'
  });
  const [statsData, setStatsData] = useState<any>({});
  const [worstCombinations, setWorstCombinations] = useState<CombinationStats[]>([]);
  const [modeStats, setModeStats] = useState({
    forward: { accuracy: '-%', total: '0', avgTime: '-s' },
    inverse: { accuracy: '-%', total: '0', avgTime: '-s' },
    combined: { accuracy: '-%', total: '0', avgTime: '-s' }
  });
  const [modeStatsData, setModeStatsData] = useState({
    forward: {},
    inverse: {}
  });
  const [selectedStatsMode, setSelectedStatsMode] = useState<ChallengeType | 'combined'>(ChallengeType.FORWARD);
  const [buttonsState, setButtonsState] = useState({ startDisabled: false, stopDisabled: true });
  const [startButtonText, setStartButtonText] = useState('Start');
  const [transcriptionLogs, setTranscriptionLogs] = useState<Array<{ text: string, timestamp: string, isFinal: boolean, confidence: number }>>([]);
  const [isManualStop, setIsManualStop] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [currentKey, setCurrentKey] = useState('C');
  const [challengeType, setChallengeType] = useState<ChallengeType>(ChallengeType.FORWARD);
  const inputModeRef = useRef<InputMode>('manual');
  const challengeTypeRef = useRef<ChallengeType>(ChallengeType.FORWARD);

  // Keep refs in sync with state
  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);

  useEffect(() => {
    challengeTypeRef.current = challengeType;
  }, [challengeType]);

  // Update stats function
  const updateStats = useCallback(() => {
    const overallStats = statsManagerRef.current.getOverallStats();

    setStats({
      accuracy: overallStats.totalAttempts > 0 ? `${Math.round(overallStats.accuracy * 100)}%` : '-%',
      total: overallStats.totalAttempts.toString(),
      avgTime: overallStats.averageTime > 0 ? `${(overallStats.averageTime / 1000).toFixed(1)}s` : '-s'
    });

    // Update mode-specific statistics
    const forwardStats = statsManagerRef.current.getStatsByMode(ChallengeType.FORWARD);
    const inverseStats = statsManagerRef.current.getStatsByMode(ChallengeType.INVERSE);
    const combinedStats = statsManagerRef.current.getCombinedStats();

    setModeStats({
      forward: {
        accuracy: forwardStats.totalAttempts > 0 ? `${Math.round(forwardStats.accuracy * 100)}%` : '-%',
        total: forwardStats.totalAttempts.toString(),
        avgTime: forwardStats.averageTime > 0 ? `${(forwardStats.averageTime / 1000).toFixed(1)}s` : '-s'
      },
      inverse: {
        accuracy: inverseStats.totalAttempts > 0 ? `${Math.round(inverseStats.accuracy * 100)}%` : '-%',
        total: inverseStats.totalAttempts.toString(),
        avgTime: inverseStats.averageTime > 0 ? `${(inverseStats.averageTime / 1000).toFixed(1)}s` : '-s'
      },
      combined: {
        accuracy: combinedStats.totalAttempts > 0 ? `${Math.round(combinedStats.accuracy * 100)}%` : '-%',
        total: combinedStats.totalAttempts.toString(),
        avgTime: combinedStats.averageTime > 0 ? `${(combinedStats.averageTime / 1000).toFixed(1)}s` : '-s'
      }
    });

    // Update mode-specific stats data for heatmaps
    setModeStatsData({
      forward: statsManagerRef.current.getStatsByModeData(ChallengeType.FORWARD),
      inverse: statsManagerRef.current.getStatsByModeData(ChallengeType.INVERSE)
    });

    // Update worst combinations based on selected mode
    let worstCombinations: CombinationStats[];
    if (selectedStatsMode === 'combined') {
      worstCombinations = statsManagerRef.current.getWorstCombinations(3);
    } else {
      worstCombinations = statsManagerRef.current.getWorstCombinationsByMode(selectedStatsMode, 3);
    }
    setWorstCombinations(worstCombinations);

    // Get raw stats data for heatmap (legacy)
    setStatsData(statsManagerRef.current.getAllStats());
  }, [selectedStatsMode]);

  // Create React callbacks for UI manager
  const reactCallbacks = useMemo(() => ({
    setStatus: (state: string, message: string) => {
      setStatus({ state, message });
    },
    setChallengeText: (text: string) => {
      setChallengeText(text);
    },
    setRecordingVisual: (active: boolean) => {
      setIsRecording(active);
    },
    setButtonsState: (startDisabled: boolean, stopDisabled: boolean) => {
      setButtonsState({ startDisabled, stopDisabled });
    },
    updateStartButtonText: (isPaused: boolean) => {
      setStartButtonText(isPaused ? 'Continue' : 'Start');
    },
    updateDisplay: () => {
      updateStats();
    },
    displayResult: (parsedAnswer: string | null, isCorrect: boolean, responseTime: number, transcription: string | null = null, correctAnswer: string | null = null, challengeType?: string) => {
      const resultIcon = isCorrect ? '✓' : '✗';

      // Show both transcription and parsed answer for debugging
      let displayText = parsedAnswer || 'Unknown';
      if (inputMode === 'audio' && transcription && transcription !== parsedAnswer) {
        displayText += ` (heard: "${transcription}")`;
      }

      // If incorrect, show what the correct answer should have been with mode-specific context
      if (!isCorrect && correctAnswer) {
        const answerType = challengeType === 'inverse' ? 'key' : 'note';
        displayText += ` (correct ${answerType}: ${correctAnswer})`;
      }

      setLastResult({
        text: `${displayText} → ${resultIcon}`,
        time: `${(responseTime / 1000).toFixed(1)}s`
      });

      // Mode-specific feedback messages
      let feedbackMessage = isCorrect ? 'Correct' : 'Incorrect';
      if (!isCorrect && challengeType) {
        if (challengeType === 'inverse') {
          feedbackMessage = 'Incorrect key';
        } else {
          feedbackMessage = 'Incorrect note';
        }
      }

      setStatus({ state: 'result', message: `${feedbackMessage} - Next challenge coming...` });
    },
    setChallengeKey: (key: string) => {
      setCurrentKey(key);
    }
  }), [updateStats]);

  // Initialize managers
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Check browser support
        const support = SpeechRecognizer.checkSupport();
        const audioSupport = AudioManager.isSupported();

        // For testing purposes, assume support
        const forceSupport = true;
        const effectiveSupport = forceSupport ? { supported: true } : support;
        const effectiveAudioSupport = forceSupport ? { speechSynthesis: true } : audioSupport;

        const missingFeatures = [];
        if (!effectiveAudioSupport.speechSynthesis) missingFeatures.push('Text-to-Speech');
        if (!effectiveSupport.supported) missingFeatures.push('Speech Recognition');

        if (missingFeatures.length > 0) {
          setStatus({ state: 'error', message: `Your browser does not support the required features: ${missingFeatures.join(', ')}. Please use a modern browser like Chrome, Firefox, or Safari.` });
          setIsInitialized(true);
          return;
        }

        // Initialize UI manager with React callbacks
        uiManagerRef.current = new UIManager(statsManagerRef.current, reactCallbacks);

        // Initialize managers
        speechManagerRef.current = new SpeechManager(uiManagerRef.current);
        challengeManagerRef.current = new ChallengeManager(
          audioManagerRef.current,
          statsManagerRef.current,
          uiManagerRef.current
        );
        sessionManagerRef.current = new SessionManager(
          uiManagerRef.current,
          audioManagerRef.current,
          speechManagerRef.current
        );

        // Set up callbacks
        setupCallbacks();

        // Initial stats update
        updateStats();

        setIsInitialized(true);
        console.log('Scale Degree Trainer initialized (React mode)');
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setStatus({ state: 'error', message: 'Failed to initialize' });
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      if (sessionManagerRef.current) {
        sessionManagerRef.current.stop();
      }
      audioManagerRef.current.cleanup();
      speechManagerRef.current?.cleanup();
    };
  }, [reactCallbacks, updateStats]);

  const setupCallbacks = useCallback(() => {
    if (!speechManagerRef.current || !challengeManagerRef.current || !sessionManagerRef.current) return;

    // Audio manager callbacks
    audioManagerRef.current.onTTSStart = (_text: string) => {
      if (inputModeRef.current === 'audio') {
        setStatus({ state: 'speaking', message: 'Speaking...' });
      } else {
        // In manual mode, enable input immediately when TTS starts
        sessionManagerRef.current!.setChallengeStartTime(Date.now());
        setStatus({ state: 'recording', message: 'Select the correct note...' });
      }
    };

    audioManagerRef.current.onTTSEnd = () => {
      // Start timing and listening when TTS ends (user can now respond)
      // But don't auto-start if manually stopped
      if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsProcessing()) {
        sessionManagerRef.current!.setChallengeStartTime(Date.now());

        if (inputModeRef.current === 'audio') {
          console.log('TTS ended, starting audio listening...');
          startListening();
        } else {
          // In manual mode, just set status to recording to show note buttons
          console.log('TTS ended, showing note buttons for manual input...');
          setStatus({ state: 'recording', message: 'Select the correct note...' });
        }
      }
    };

    audioManagerRef.current.onError = (error: string) => {
      handleError(error);
    };

    // Speech manager callbacks (only needed for audio mode)
    if (speechManagerRef.current) {
      speechManagerRef.current.setCallbacks(
        (text: string | null, _fullResult: any) => {
          onTranscriptionComplete(text, _fullResult);
        },
        (text: string | null, isFinal: boolean, confidence: number) => {
          onTranscriptionUpdate(text, isFinal, confidence);
        },
        (error: string, shouldRetry: boolean) => {
          onSpeechError(error, shouldRetry);
        }
      );
    }
  }, []);

  const onTranscriptionComplete = useCallback(async (text: string | null, _fullResult: any) => {
    if (!sessionManagerRef.current || !challengeManagerRef.current) return;

    // Check if we're already processing
    if (sessionManagerRef.current.getIsProcessing()) {
      console.log('Already processing, ignoring duplicate transcription');
      return;
    }

    sessionManagerRef.current.setIsProcessing(true);
    const result = await challengeManagerRef.current.processTranscriptionResult(text, sessionManagerRef.current.getChallengeStartTime(), false);

    // Wait for TTS to complete if present (incorrect answers)
    if (result.ttsPromise) {
      await result.ttsPromise;
    }

    // Only schedule next challenge if not manually stopped
    if (!isManualStop) {
      challengeManagerRef.current.scheduleNextChallenge(result.parsedNote, sessionManagerRef.current.getIsRunning(), sessionManagerRef.current.getIsPaused(), () => {
        sessionManagerRef.current!.setIsProcessing(false);
      });
    } else {
      sessionManagerRef.current!.setIsProcessing(false);
    }
  }, []);

  const onTranscriptionUpdate = useCallback((text: string | null, isFinal: boolean, confidence: number) => {
    if (text && text.trim()) {
      const timestamp = new Date().toLocaleTimeString();
      const logEntry = {
        text: text.trim(),
        timestamp,
        isFinal,
        confidence
      };

      setTranscriptionLogs(prevLogs => {
        const newLogs = [...prevLogs, logEntry];
        // Keep only the last 50 entries
        return newLogs.slice(-50);
      });
    }
  }, []);

  const onSpeechError = useCallback((error: string, shouldRetry: boolean) => {
    if (!sessionManagerRef.current || !challengeManagerRef.current) return;

    sessionManagerRef.current.incrementConsecutiveErrors();

    // Check for timeout or "are you still there?" situation
    if (error.includes('30 seconds') || error.includes('still there')) {
      sessionManagerRef.current.enterPausedState();
      return;
    }

    // Check if we should show error or try to continue
    if (sessionManagerRef.current.getIsRunning() && !error.includes('stopped') && !error.includes('aborted')) {

      if (sessionManagerRef.current.shouldStopDueToErrors()) {
        // Too many consecutive errors - stop and require manual restart
        setStatus({ state: 'error', message: `Multiple speech recognition failures. Please check your microphone and try again.` });
        sessionManagerRef.current.stop();
      } else if (shouldRetry && sessionManagerRef.current.getConsecutiveErrors() < sessionManagerRef.current.getMaxConsecutiveErrors()) {
        // Limited retry for certain types of errors
        setStatus({ state: 'error', message: `Speech recognition failed. Trying again... (${sessionManagerRef.current.getConsecutiveErrors()}/${sessionManagerRef.current.getMaxConsecutiveErrors()})` });
        setTimeout(() => {
          if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsProcessing() && !sessionManagerRef.current!.getIsListening() && !sessionManagerRef.current!.getIsPaused()) {
            startListening();
          }
        }, 2000);
      } else {
        // Show error and move to next challenge
        setStatus({ state: 'error', message: 'No speech detected. Moving to next challenge...' });
        setTimeout(() => {
          if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsPaused()) {
            sessionManagerRef.current!.setIsProcessing(false);
            challengeManagerRef.current!.generateNewChallenge(sessionManagerRef.current!.getIsRunning(), sessionManagerRef.current!.getIsPaused());
          }
        }, 2000);
      }
    } else if (sessionManagerRef.current.getIsRunning() && !sessionManagerRef.current.getIsPaused()) {
      // Normal operation - just show brief error and continue
      setStatus({ state: 'error', message: 'Recognition stopped. Next challenge coming...' });
      setTimeout(() => {
        if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsPaused()) {
          sessionManagerRef.current!.setIsProcessing(false);
          challengeManagerRef.current!.generateNewChallenge(sessionManagerRef.current!.getIsRunning(), sessionManagerRef.current!.getIsPaused());
        }
      }, 1500);
    }
  }, []);

  const startListening = useCallback(async () => {
    // Only start listening in audio mode
    if (inputModeRef.current !== 'audio') return;

    if (!sessionManagerRef.current || !speechManagerRef.current || !challengeManagerRef.current) return;

    if (isManualStop || !sessionManagerRef.current.getIsRunning() || sessionManagerRef.current.getIsProcessing() || sessionManagerRef.current.getIsListening() || sessionManagerRef.current.getIsPaused()) {
      console.log(`Cannot start listening: manualStop=${isManualStop}, running=${sessionManagerRef.current.getIsRunning()}, processing=${sessionManagerRef.current.getIsProcessing()}, listening=${sessionManagerRef.current.getIsListening()}, paused=${sessionManagerRef.current.getIsPaused()}`);
      return;
    }

    // Check if we've had too many consecutive errors
    if (sessionManagerRef.current.shouldStopDueToErrors()) {
      console.log('Too many consecutive errors, stopping session');
      sessionManagerRef.current.stop();
      return;
    }

    // Create answer validator for early acceptance
    const currentChallenge = challengeManagerRef.current.getCurrentChallenge();
    const answerValidator = currentChallenge ? (transcript: string) => {
      if (currentChallenge.type === ChallengeType.FORWARD) {
        const parsedNote = MusicTheory.parseNoteName(transcript);
        return MusicTheory.isCorrectAnswer(
          currentChallenge.key,
          currentChallenge.degree,
          parsedNote
        );
      } else {
        const parsedKey = MusicTheory.parseKeyName(transcript);
        return MusicTheory.isValidKeyAnswer(currentChallenge.correctAnswer, parsedKey);
      }
    } : undefined;

    try {
      await speechManagerRef.current.startListening(answerValidator);
    } catch (error) {
      console.error('Failed to start listening:', error);
      sessionManagerRef.current.incrementConsecutiveErrors();

      if (sessionManagerRef.current.getIsRunning()) {
        if (sessionManagerRef.current.shouldStopDueToErrors()) {
          handleError('Multiple failures starting speech recognition. Please check your microphone.');
          sessionManagerRef.current.stop();
        } else {
          handleError('Failed to start listening. Trying again...');
          // Only retry once, then move to next challenge
          setTimeout(() => {
            if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsProcessing() && !sessionManagerRef.current!.getIsListening() && !sessionManagerRef.current!.getIsPaused()) {
              challengeManagerRef.current!.generateNewChallenge(sessionManagerRef.current!.getIsRunning(), sessionManagerRef.current!.getIsPaused());
            }
          }, 2000);
        }
      }
    }
  }, []);

  const handleError = useCallback((message: string) => {
    console.error('App error:', message);
    setStatus({ state: 'error', message });

    // Don't auto-recover if we've had too many errors
    if (sessionManagerRef.current && sessionManagerRef.current.getConsecutiveErrors() >= sessionManagerRef.current.getMaxConsecutiveErrors()) {
      console.log('Too many consecutive errors, not auto-recovering');
      return;
    }

    // Auto-recovery after error (but not if manually stopped)
    if (sessionManagerRef.current && sessionManagerRef.current.getIsRunning() && !sessionManagerRef.current.getIsPaused() && !isManualStop) {
      setTimeout(() => {
        if (!isManualStop && sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsProcessing() && !sessionManagerRef.current!.getIsPaused()) {
          challengeManagerRef.current!.generateNewChallenge(sessionManagerRef.current!.getIsRunning(), sessionManagerRef.current!.getIsPaused());
        }
      }, 3000);
    }
  }, []);

  // Event handlers
  const handleStartContinue = useCallback(async () => {
    if (!sessionManagerRef.current || !challengeManagerRef.current) return;

    if (sessionManagerRef.current.getIsPaused()) {
      sessionManagerRef.current.resumeFromPause();
    } else {
      sessionManagerRef.current.start();

      // Only initialize speech recognizer for audio mode
      if (inputModeRef.current === 'audio') {
        if (!speechManagerRef.current) return;

        // Initialize speech recognizer if not already done
        if (!speechManagerRef.current.isReady()) {
          try {
            await speechManagerRef.current.initializeSpeechRecognition();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            handleError(`Failed to initialize speech recognition: ${errorMessage}`);
            return;
          }
        }
      }

      // Start first challenge
      challengeManagerRef.current.generateNewChallenge(sessionManagerRef.current.getIsRunning(), sessionManagerRef.current.getIsPaused());
    }
  }, [handleError]);

  const handleStop = useCallback(() => {
    if (sessionManagerRef.current) {
      console.log('Stopping session manually');
      setIsManualStop(true);
      sessionManagerRef.current.stop();

      // Immediately update UI state to prevent race conditions
      setButtonsState({
        startDisabled: false,
        stopDisabled: true
      });
      setStartButtonText('Start');
      setIsRecording(false);
      setStatus({ state: 'idle', message: 'Stopped' });

      // Clear manual stop flag after a short delay to allow for cleanup
      setTimeout(() => setIsManualStop(false), 1000);
    }
  }, []);

  const handleResetStats = useCallback(() => {
    if (confirm('Are you sure you want to reset all statistics? This cannot be undone.')) {
      statsManagerRef.current.resetStats();
      updateStats();
      setStatus({ state: 'idle', message: 'Statistics reset' });
    }
  }, [updateStats]);

  const handleResetStatsByMode = useCallback((mode: ChallengeType) => {
    const modeText = mode === ChallengeType.FORWARD ? 'Forward' : 'Inverse';
    if (confirm(`Are you sure you want to reset ${modeText} mode statistics? This cannot be undone.`)) {
      statsManagerRef.current.resetStatsByMode(mode);
      updateStats();
      setStatus({ state: 'idle', message: `${modeText} mode statistics reset` });
    }
  }, [updateStats]);

  const handleStatsModeChange = useCallback((mode: ChallengeType | 'combined') => {
    setSelectedStatsMode(mode);
  }, []);

  const handleInputModeChange = useCallback((mode: InputMode) => {
    // Only allow mode change when not running
    if (!sessionManagerRef.current?.getIsRunning()) {
      setInputMode(mode);
      inputModeRef.current = mode;
      console.log(`Input mode changed to: ${mode}`);
    }
  }, []);

  const handleChallengeTypeChange = useCallback((type: ChallengeType) => {
    // Only allow challenge type change when not running
    if (!sessionManagerRef.current?.getIsRunning() && challengeManagerRef.current) {
      setChallengeType(type);
      challengeTypeRef.current = type;
      challengeManagerRef.current.setChallengeType(type);
      console.log(`Challenge type changed to: ${type}`);
    }
  }, []);

  const handleNoteSelect = useCallback(async (note: string) => {
    if (!sessionManagerRef.current || !challengeManagerRef.current) return;

    console.log(`Note selected: ${note}, inputMode: ${inputModeRef.current}, status: ${status.state}`);

    // Only process note selection in manual mode when recording and in forward mode
    if (inputModeRef.current !== 'manual' || status.state !== 'recording' || challengeTypeRef.current !== ChallengeType.FORWARD) {
      console.log('Note selection ignored - not in manual recording mode or not forward mode');
      return;
    }

    console.log('Processing manual note selection...');
    // Process the manual input as if it were a transcription
    await onTranscriptionComplete(note, null);
  }, [status.state, onTranscriptionComplete]);

  const handleKeySelect = useCallback(async (key: string) => {
    if (!sessionManagerRef.current || !challengeManagerRef.current) return;

    console.log(`Key selected: ${key}, inputMode: ${inputModeRef.current}, status: ${status.state}`);

    // Only process key selection in manual mode when recording and in inverse mode
    if (inputModeRef.current !== 'manual' || status.state !== 'recording' || challengeTypeRef.current !== ChallengeType.INVERSE) {
      console.log('Key selection ignored - not in manual recording mode or not inverse mode');
      return;
    }

    console.log('Processing manual key selection...');
    // Process the manual input as if it were a transcription
    await onTranscriptionComplete(key, null);
  }, [status.state, onTranscriptionComplete]);

  // Update UI state when session manager changes
  useEffect(() => {
    if (!sessionManagerRef.current) return;

    const updateUI = () => {
      setButtonsState({
        startDisabled: sessionManagerRef.current!.getIsRunning() && !sessionManagerRef.current!.getIsPaused(),
        stopDisabled: !sessionManagerRef.current!.getIsRunning()
      });
      setStartButtonText(sessionManagerRef.current!.getIsPaused() ? 'Continue' : 'Start');
      // Only show recording state in audio mode
      setIsRecording(inputModeRef.current === 'audio' && sessionManagerRef.current!.getIsListening());
    };

    // Initial update
    updateUI();

    // Set up interval to check for changes (since we can't observe the managers directly)
    const interval = setInterval(updateUI, 100);
    return () => clearInterval(interval);
  }, [isInitialized]);

  return {
    isInitialized,
    challengeText,
    status,
    isRecording,
    lastResult,
    stats,
    statsData,
    worstCombinations,
    modeStats,
    modeStatsData,
    selectedStatsMode,
    buttonsState,
    startButtonText,
    transcriptionLogs,
    inputMode,
    currentKey,
    challengeType,
    handleStartContinue,
    handleStop,
    handleResetStats,
    handleResetStatsByMode,
    handleStatsModeChange,
    handleInputModeChange,
    handleChallengeTypeChange,
    handleNoteSelect,
    handleKeySelect
  };
};